import { useEffect, useState } from "react";
import {
    Plus,
    Search,
    Upload,
    Loader2,
    Pencil,
    Trash,
    ChevronLeft,
    ChevronRight,
    AlertTriangle
} from "lucide-react";

import * as XLSX from "xlsx";
import Papa from "papaparse";
import Cookies from "js-cookie";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
    CardFooter
} from "@/components/ui/card";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from "@/components/ui/dialog";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";

import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";

type Book = {
    id: string
    name: string
    author: string
    genre: string
    index: number
}

const emptyBook: Book = {
    id: "",
    name: "",
    author: "",
    genre: "",
    index: -1
}

export default function LibraryPage() {

    const [ books, setBooks ] = useState<Book[]>([]);
    const [ search, setSearch ] = useState("");

    const [ open, setOpen ] = useState(false);
    const [ mode, setMode ] = useState<"manual" | "bulk">("manual");

    const [ form, setForm ] = useState<Book>(emptyBook);

    const [ headers, setHeaders ] = useState<string[]>([]);
    const [ file, setFile ] = useState<File | null>(null);

    const [ mapId, setMapId ] = useState("");
    const [ mapName, setMapName ] = useState("");
    const [ mapAuthor, setMapAuthor ] = useState("");
    const [ mapGenre, setMapGenre ] = useState("");

    const [ editingIndex, setEditingIndex ] = useState<number | null>(null);

    // Separate loading states: table fetch vs dialog submit vs delete
    const [ fetchLoading, setFetchLoading ] = useState(false);
    const [ submitLoading, setSubmitLoading ] = useState(false);
    const [ deleteTarget, setDeleteTarget ] = useState<Book | null>(null);
    const [ deleteLoading, setDeleteLoading ] = useState(false);

    const [ currentPage, setCurrentPage ] = useState(1);
    const [ rowsPerPage, setRowsPerPage ] = useState(5);

    const handleRowsPerPageChange = (value: string) => {
        setRowsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const [ pagination, setPagination ] = useState({
        page: 1,
        pageSize: 5,
        totalRecords: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
    });

    /* ---------------- FETCH BOOKS ---------------- */

    const fetchBooks = async () => {
        try {
            setFetchLoading(true)

            const res = await fetch(
                `${environment.BASE_URL}/campusLibrary/search`,
                {
                    method: "POST",
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        parent: Cookies.get("ins_code"),
                        bookName: search,
                        page: currentPage,
                        pageSize: rowsPerPage
                    })
                }
            )

            const data = await res.json()

            if (!data.success) return

            const mapped = (data.data || []).map((b: any) => ({
                id: b.book_id ?? "",
                name: b.name ?? "",
                author: b.author ?? "",
                genre: b.genre ?? "",
                index: b.index
            }))

            setBooks(mapped)

            if (data.pagination) {
                setPagination(data.pagination)
            }

        } catch (err) {
            console.error("Fetch books failed", err)
        } finally {
            setFetchLoading(false)
        }
    }

    useEffect(() => {
        fetchBooks()
    }, [ currentPage, rowsPerPage, search ])

    /* ---------------- ADD / UPDATE ---------------- */

    const handleSubmit = async () => {
        try {
            setSubmitLoading(true)

            const endpoint = editingIndex !== null
                ? `${environment.BASE_URL}/campusLibrary/update`
                : `${environment.BASE_URL}/campusLibrary/add`

            const res = await fetch(endpoint, {
                method: "POST",
                headers: apiHeaders(),
                body: JSON.stringify({
                    parent: Cookies.get("ins_code"),
                    bookId: form.id,
                    name: form.name,
                    author: form.author,
                    genre: form.genre,
                    ...(editingIndex !== null && { index: form.index })
                })
            })

            const result = await res.json()

            if (!result.success) {
                alert(result.message)
                return
            }

            fetchBooks()
            setOpen(false)
        } catch (err) {
            console.error(err)
        } finally {
            setSubmitLoading(false)
        }
    }

    /* ---------------- DELETE ---------------- */

    const handleDelete = async () => {
        if (!deleteTarget) return

        try {
            setDeleteLoading(true)

            const res = await fetch(
                `${environment.BASE_URL}/campusLibrary/delete`,
                {
                    method: "POST",
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        parent: Cookies.get("ins_code"),
                        index: deleteTarget.index
                    })
                }
            )

            const result = await res.json()

            if (!result.success) {
                alert(result.message)
                return
            }

            setDeleteTarget(null)
            fetchBooks()

        } catch (err) {
            console.error(err)
        } finally {
            setDeleteLoading(false)
        }
    }

    /* ---------------- FILE PARSING ---------------- */

    const parseCSV = (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (result: any) => {
                const rows = result.data
                const headers = Object.keys(rows[ 0 ] || {})
                setHeaders(headers)
            }
        })
    }

    const parseExcel = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e: any) => {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: "array" })
            const sheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ]
            const json = XLSX.utils.sheet_to_json(sheet)
            const headers = Object.keys(json[ 0 ] || {})
            setHeaders(headers)
        }
        reader.readAsArrayBuffer(file)
    }

    const handleFile = (f: File) => {
        setFile(f)
        const ext = f.name.split(".").pop()?.toLowerCase()
        if (ext === "csv") parseCSV(f)
        if (ext === "xls" || ext === "xlsx") parseExcel(f)
    }

    /* ---------------- BULK IMPORT ---------------- */

    const handleBulkImport = async () => {
        if (!file || !mapId || !mapName) {
            alert("Please map required fields")
            return
        }

        try {
            setSubmitLoading(true)

            const formData = new FormData()
            formData.append("file", file)
            formData.append("parent", Cookies.get("ins_code") || "")
            formData.append("mapId", mapId)
            formData.append("mapName", mapName)
            formData.append("mapAuthor", mapAuthor)
            formData.append("mapGenre", mapGenre)

            const headers: any = apiHeaders()
            delete headers[ "Content-Type" ]

            const res = await fetch(
                `${environment.BASE_URL}/campusLibrary/bulkAdd`,
                { method: "POST", headers, body: formData }
            )

            const result = await res.json()

            if (result.success) {
                fetchBooks()
                setOpen(false)
            } else {
                alert(result.message || "Import failed")
            }
        } catch (err) {
            console.error(err)
        } finally {
            setSubmitLoading(false)
        }
    }

    /* ---------------- UI ---------------- */

    return (

        <>
            <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8">

                <div className="mb-8 flex items-center justify-between">

                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Library Management</h1>
                        <p className="text-sm text-muted-foreground">
                            Manage books and bulk import catalog records
                        </p>
                    </div>

                    <Button
                        className="gap-2"
                        onClick={() => {
                            setForm(emptyBook)
                            setEditingIndex(null)
                            setMode("manual")
                            setOpen(true)
                        }}
                    >
                        <Plus className="h-4 w-4" />
                        Add Book
                    </Button>

                </div>

                <Card className="border border-border/60 shadow-sm overflow-hidden">

                    <CardHeader className="flex flex-row items-center justify-between">

                        <div>
                            <CardTitle>Library Books</CardTitle>
                            <CardDescription>{pagination.totalRecords} records found</CardDescription>
                        </div>

                        <div className="relative w-64">

                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                            <Input
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value)
                                    setCurrentPage(1)
                                }}
                                placeholder="Search books..."
                                className="pl-9"
                            />

                        </div>

                    </CardHeader>

                    <CardContent className="p-0">

                        <Table>

                            <TableHeader>
                                <TableRow>
                                    <TableHead className="pl-6">Book ID</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Author</TableHead>
                                    <TableHead>Genre</TableHead>
                                    <TableHead className="pr-6 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>

                                {fetchLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-24 text-center">
                                            <div className="flex items-center justify-center gap-2 text-muted-foreground">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading books...
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : books.length > 0 ? books.map((b, i) => (

                                    <TableRow key={b.id + i}>

                                        <TableCell className="pl-6 font-medium">{b.id}</TableCell>
                                        <TableCell>{b.name}</TableCell>
                                        <TableCell>{b.author}</TableCell>

                                        <TableCell>
                                            <Badge variant="secondary">{b.genre}</Badge>
                                        </TableCell>

                                        <TableCell className="pr-6 text-right">
                                            <div className="flex justify-end gap-2">

                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => {
                                                        setForm(b)
                                                        setEditingIndex(i)
                                                        setOpen(true)
                                                    }}
                                                >
                                                    <Pencil className="h-4 w-4 text-muted-foreground" />
                                                </Button>

                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => setDeleteTarget(b)}
                                                >
                                                    <Trash className="h-4 w-4 text-destructive" />
                                                </Button>

                                            </div>
                                        </TableCell>

                                    </TableRow>

                                )) : (

                                    <TableRow>
                                        <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                                            No books found.
                                        </TableCell>
                                    </TableRow>

                                )}

                            </TableBody>

                        </Table>

                    </CardContent>

                    <CardFooter className="flex flex-col sm:flex-row items-center justify-between border-t px-6 py-4 gap-4">

                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">Rows per page</p>
                                <Select
                                    value={rowsPerPage.toString()}
                                    onValueChange={handleRowsPerPageChange}
                                >
                                    <SelectTrigger className="h-8 w-[70px]">
                                        <SelectValue placeholder={rowsPerPage} />
                                    </SelectTrigger>
                                    <SelectContent side="top">
                                        {[ 5, 10, 20, 50 ].map((size) => (
                                            <SelectItem key={size} value={size.toString()}>
                                                {size}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <Separator orientation="vertical" className="h-4 hidden sm:block" />

                            <div className="text-sm text-muted-foreground">
                                Showing {pagination.totalRecords === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1}
                                {" "}to{" "}
                                {Math.min(pagination.page * pagination.pageSize, pagination.totalRecords)}
                                {" "}of {pagination.totalRecords}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={!pagination.hasPrev || fetchLoading}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <div className="text-sm font-medium min-w-[100px] text-center">
                                Page {pagination.page} of {pagination.totalPages || 1}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={!pagination.hasNext || fetchLoading}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>

                    </CardFooter>

                </Card>

            </div>

            {/* ── Add / Edit Book Dialog ── */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[520px]">

                    <DialogHeader>
                        <DialogTitle>
                            {editingIndex !== null ? "Edit Book" : mode === "manual" ? "Add Book" : "Bulk Import"}
                        </DialogTitle>
                    </DialogHeader>

                    {editingIndex === null && (
                        <div className="flex gap-2 border-b pb-3">
                            <Button
                                variant={mode === "manual" ? "default" : "outline"}
                                size="sm"
                                onClick={() => setMode("manual")}
                            >
                                Manual
                            </Button>
                            <Button
                                variant={mode === "bulk" ? "default" : "outline"}
                                size="sm"
                                onClick={() => setMode("bulk")}
                            >
                                <Upload className="h-4 w-4 mr-1" />
                                Bulk Import
                            </Button>
                        </div>
                    )}

                    {mode === "manual" || editingIndex !== null ? (
                        <div className="grid gap-4 py-2">
                            <div className="grid gap-1.5">
                                <Label>Book ID</Label>
                                <Input
                                    value={form.id}
                                    onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                                    placeholder="e.g. BK-001"
                                    disabled={editingIndex !== null}
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Name</Label>
                                <Input
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="Book title"
                                    disabled={submitLoading}
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Author</Label>
                                <Input
                                    value={form.author}
                                    onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                                    placeholder="Author name"
                                    disabled={submitLoading}
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Genre</Label>
                                <Input
                                    value={form.genre}
                                    onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
                                    placeholder="e.g. Science, History"
                                    disabled={submitLoading}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="grid gap-4 py-2">
                            <div className="grid gap-1.5">
                                <Label>Upload File (CSV / XLSX)</Label>
                                <Input
                                    type="file"
                                    accept=".csv,.xls,.xlsx"
                                    disabled={submitLoading}
                                    onChange={e => e.target.files?.[ 0 ] && handleFile(e.target.files[ 0 ])}
                                />
                            </div>
                            {headers.length > 0 && (
                                <>
                                    <p className="text-sm text-muted-foreground">Map your columns:</p>
                                    {[
                                        { label: "Book ID *", value: mapId, set: setMapId },
                                        { label: "Name *", value: mapName, set: setMapName },
                                        { label: "Author", value: mapAuthor, set: setMapAuthor },
                                        { label: "Genre", value: mapGenre, set: setMapGenre },
                                    ].map(({ label, value, set }) => (
                                        <div key={label} className="grid gap-1.5">
                                            <Label>{label}</Label>
                                            <Select value={value} onValueChange={set} disabled={submitLoading}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select column" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {headers.map(h => (
                                                        <SelectItem key={h} value={h}>{h}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={submitLoading}>
                            Cancel
                        </Button>
                        {mode === "bulk" && editingIndex === null ? (
                            <Button onClick={handleBulkImport} disabled={submitLoading}>
                                {submitLoading
                                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                                    : "Import"
                                }
                            </Button>
                        ) : (
                            <Button
                                disabled={submitLoading || !form.id || !form.name}
                                onClick={handleSubmit}
                            >
                                {submitLoading
                                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingIndex !== null ? "Saving..." : "Adding..."}</>
                                    : editingIndex !== null ? "Save Changes" : "Add Book"
                                }
                            </Button>
                        )}
                    </DialogFooter>

                </DialogContent>
            </Dialog>

            {/* ── Delete Confirmation Dialog ── */}
            <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open && !deleteLoading) setDeleteTarget(null) }}>
                <DialogContent className="sm:max-w-[420px]">

                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Delete Book
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. The book will be permanently removed from the library.
                        </DialogDescription>
                    </DialogHeader>

                    {deleteTarget && (
                        <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">
                            <p><span className="font-medium">ID:</span> {deleteTarget.id}</p>
                            <p><span className="font-medium">Name:</span> {deleteTarget.name}</p>
                            <p><span className="font-medium">Author:</span> {deleteTarget.author}</p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading
                                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                                : "Delete"
                            }
                        </Button>
                    </DialogFooter>

                </DialogContent>
            </Dialog>

        </>
    )

}
